import { UpdateSession, useSession } from 'next-auth/react'
import { useCallback, useEffect, useRef } from 'react'
import { common } from '@smokeballdev/smokeball-client-sdk'
import { useSmokeballSdkStore } from '@/lib/store/use-smokeball-sdk-store'

// Polling the session every 1 hour
const REFRESH_INTERVAL = 1000 * 60 * 60
const MAX_TOKEN_FETCH_ATTEMPTS = 20

export const getTokenExpiry = (token: string) => {
  const parts = token.split('.')
  if (parts.length !== 3) {
    console.log('invalid token', token)
    return null
  }

  let decodedToken: any;
  try {
    decodedToken = JSON.parse(atob(parts[1]))
  } catch (e) {
    console.log('invalid token (couldnt parse)', token)
    return null
  }

  if (!decodedToken.exp || typeof decodedToken.exp !== 'number') {
    console.log('invalid token (exp)', token)
    return null
  }

  return decodedToken.exp * 1000
}

export const isTokenExpired = (token: string) => {
  const expiry = getTokenExpiry(token)
  if (expiry == null) {
    return true
  }

  return expiry < Date.now()
}

const updateSession = async (
  sdk: common.Api | undefined,
  update: UpdateSession,
  status: "authenticated" | "loading" | "unauthenticated",
  isEmbedded: boolean
) => {
  // Only refresh tokens when embedded in Smokeball (not in web browser)
  if (sdk?.auth != null && isEmbedded && !process.env.NEXT_PUBLIC_FAKE_SB_SDK) {
    try {
      let token: string | null = null
      let attempt = 0
      while (true) {
        token = await sdk.auth.idToken()
        if (!isTokenExpired(token)) {
          break
        }

        if (attempt > MAX_TOKEN_FETCH_ATTEMPTS) {
          throw new Error(`unable to get token after ${MAX_TOKEN_FETCH_ATTEMPTS} attempts`)
        }
        console.log('token expired, retry after 1s')
        attempt += 1
        await new Promise((resolve) => setTimeout(resolve, 1000))
      }

      const tokenExpiry = getTokenExpiry(token)
      console.log('updated token, expiry:', tokenExpiry ? new Date(tokenExpiry).toLocaleString() : 'unknown', 'status:', status)
      let updateAttempt = 0
      while (true) {
        try {
          const updateResult = await update({ token })
          if (!updateResult) {
            throw new Error(`token update failed to return session`)
          }
          break
        } catch (updateError) {
          if (updateAttempt > MAX_TOKEN_FETCH_ATTEMPTS) {
            throw new Error(`unable to call token update after ${MAX_TOKEN_FETCH_ATTEMPTS} attempts: ${updateError}`)
          }
          console.warn('token update failed, retry after 1s', updateError)
          updateAttempt += 1
          await new Promise((resolve) => setTimeout(resolve, 1000))
        }
      }
    } catch (error) {
      console.log('unable to get token', error)
    }
  }
}

export default function RefreshSdkToken() {
  const sdk = useSmokeballSdkStore((store) => store.sdk)
  const isAvailable = useSmokeballSdkStore((store) => store.isAvailable)
  const isEmbedded = useSmokeballSdkStore((store) => store.isEmbedded)
  const { update, status } = useSession()
  const statusRef = useRef(status)
  const updateRef = useRef(update)
  const isEmbeddedRef = useRef(isEmbedded)

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    updateRef.current = update;
  }, [update]);

  useEffect(() => {
    isEmbeddedRef.current = isEmbedded;
  }, [isEmbedded]);

  const refreshToken = useCallback(async () => {
    if (statusRef.current !== 'loading') {
      updateSession(sdk, updateRef.current, statusRef.current, isEmbeddedRef.current);
    } else {
      const interval = setInterval(() => {
        if (statusRef.current !== 'loading') {
          clearInterval(interval);
          updateSession(sdk, updateRef.current, statusRef.current, isEmbeddedRef.current);
        }
      }, 2000);
    }
  }, [sdk])

  useEffect(() => {
    if (!isAvailable) {
      return
    }

    // TIP: You can also use `navigator.onLine` and some extra event handlers
    // to check if the user is online and only update the session if they are.
    // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/onLine
    const interval = setInterval(() => refreshToken(), REFRESH_INTERVAL)

    // Need to use a timeout for initial refresh to avoid an infinite loop / error about csrf / timeout
    const timeout = setTimeout(() => refreshToken(), 5 * 1000)

    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [isAvailable, refreshToken])
  // Listen for when the page is visible, if the user switches tabs
  // and makes our tab visible again, re-fetch the session
  useEffect(() => {
    const visibilityHandler = () => {
      document.visibilityState === 'visible' && refreshToken()
    }
    window.addEventListener('visibilitychange', visibilityHandler, false)
    return () =>
      window.removeEventListener('visibilitychange', visibilityHandler, false)
  }, [refreshToken])

  return <></>
}
